/**
 * Error codes map to the eleven designed states, not to HTTP status alone
 * (docs/05 § conventions). A code exists because a screen was drawn for it.
 *
 * There is deliberately no generic fallback code. The design has no
 * "কিছু ভুল হয়েছে" screen: all eleven states say something specific, so a state
 * that cannot be named is a gap in backend information, not a reason to blur the
 * copy. See docs/10 § WP-K — a test asserts no route can reach a generic fallback.
 */
export const ERROR_CODES = [
  'E_OFFLINE',
  'E_PAYMENT_FAILED',
  'E_PROVIDER_UNAVAILABLE',
  'E_VERIFICATION_PENDING',
  'E_APPROVAL_PENDING',
  'E_SYNC',
  'E_PERMISSION',
  'E_SERVICE_PAUSED',
  'E_DOMAIN_NOT_LIVE',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/**
 * The two refusals `E_PERMISSION` was carrying as one.
 *
 * ── THE BUG THIS EXISTS TO END ───────────────────────────────────────────────
 * A customer who was simply not signed in opened অ্যাকাউন্ট and was shown the অনুমতি
 * নেই frame — drawn on ফাউন্ডার mode's dark ground, headed "ফাউন্ডার মোড", naming a
 * finance role and a cap table. Nothing in the envelope let the client tell "nobody is
 * signed in" from "this signed-in person may not see this", because both were
 * `E_PERMISSION` and the difference lived only in the HTTP status — which the state
 * layer never sees, and which the Worker does not even use consistently (a vendor route
 * answers 401 to a signed-in customer who is not a vendor).
 *
 *   no_identity   the request carried no identity the Worker could verify: no token,
 *                 an expired one, a forged one. The answer is the sign-in door, never a
 *                 refusal — there is nobody yet to refuse.
 *   role_denied   a verified identity asked for something its role or level does not
 *                 reach. This is অনুমতি নেই, and it only exists where roles differ.
 *
 * A SUB-code, not a tenth code, deliberately: the nine codes are a bijection with nine
 * drawn states (`apps/web/src/states/keys.ts`), and "not signed in" is not a state of any
 * surface — it is the absence of the person the surface would be for.
 *
 * The Worker derives it from the ACTOR, not from the status (`middleware/errors.ts`), so
 * the two can never disagree about who asked.
 */
export const ERROR_SUB_CODES = ['no_identity', 'role_denied'] as const;

export type ErrorSubCode = (typeof ERROR_SUB_CODES)[number];

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    /** Already localised. The client never composes Bangla from fragments. */
    message_bn: string;
    message_en: string;
    /** The string a user reads aloud to support, e.g. PAY-4421. */
    reference: string;
    /**
     * Which kind of `E_PERMISSION` this is. Present on every `E_PERMISSION` the Worker's
     * error handler sends; absent on the other eight codes, and on an envelope from a
     * Worker older than the field — which a client must read as "unknown", not as either.
     */
    sub_code?: ErrorSubCode;
  };
  /**
   * A refusal's own headline, when the server has one — the founder door sends it beside
   * `body_bn` (`packages/contracts § FOUNDER_REFUSAL_BN`). Already localised.
   */
  title_bn?: string;
  /** A refusal's own body, beside `title_bn`. Already localised. */
  body_bn?: string;
  /**
   * WHERE the refusal happened, in Bangla, for the top bar of অনুমতি নেই — e.g. the
   * console or mode the caller was in. Sent by the server or absent; the client never
   * fills it in, because the one it used to fill in ("ফাউন্ডার মোড") was shown to people
   * who were nowhere near founder mode.
   */
  header_bn?: string;
  /**
   * What happened to the customer's money, for the SECOND line of পেমেন্ট ব্যর্থ — the
   * line `P0 States § ৫` puts in green before any explanation. Only the server knows it:
   * a duplicate transaction id means money DID move, an unoffered method means none did,
   * and a client that printed the drawn "আপনার টাকা কাটা হয়নি।" over both would be wrong
   * about money half the time. Absent means "not said", and the line is not drawn.
   */
  money_position_bn?: string;
}

/** True when the envelope says the request had no verified identity at all. */
export function isNoIdentity(body: ApiErrorBody): boolean {
  return body.error.code === 'E_PERMISSION' && body.error.sub_code === 'no_identity';
}

/**
 * `E_DOMAIN_NOT_LIVE` is the eleventh state and carries a required payload:
 * the state's whole job is to be useful rather than apologetic (docs/05).
 */
export interface DomainNotLiveError extends ApiErrorBody {
  error: ApiErrorBody['error'] & { code: 'E_DOMAIN_NOT_LIVE' };
  alternatives: Array<{ offering_id: string; title_bn: string }>;
  notify_available: true;
}

export function isDomainNotLive(e: ApiErrorBody): e is DomainNotLiveError {
  return e.error.code === 'E_DOMAIN_NOT_LIVE';
}
